import React from 'react';
import App from 'next/app';
import Router from 'next/router';
import { I18nextProvider } from 'react-i18next';
import { ApolloProvider } from '@apollo/client';
import { ThemeProvider } from 'styled-components';
import { DefaultSeo } from 'next-seo';
import NProgress from 'nprogress';
import reduxWrapper from '../store';
import i18n from '../utils/i18n';
import { createSeoConfig } from '../utils/helpers';
import ErrorBoundary from '../components/ErrorBoundary';
import withApolloClient from '../utils/with-apollo-client';
import countersLoader from '../utils/counters';
import CONFIG from '../config';
import HeadMeta from '../components/HeadMeta/HeadMeta';
import ErrorPage from '../components/ErrorPage';
import adriverCallback from '../components/Banner/genericCallback';
import BridgeProvider, { withBridgeState } from '../bridge';
import '../styles/index.scss';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { getCurrentTheme } from '../styled/theme';
import { GlobalStyles } from '../styled/global.styled';

const { gtmContainerId, fbAppId } = CONFIG;
const { tagManager, li } = countersLoader;

const onlyProductionContent = renderContent => {
  if (window.location.hostname.indexOf('bina.az') > -1) {
    renderContent();
  }
};

class MyApp extends App {
  static async getInitialProps({ Component, ctx }) {
    let pageProps = {};
    const { store, pathname } = ctx;

    store.dispatch({ type: 'DRAWER_TOGGLE', payload: false });

    try {
      if (ctx && ctx.res) {
        ctx.res.setHeader(
          'Cache-Control',
          's-maxage=1, stale-while-revalidate=59, max-age=0, private, must-revalidate'
        );
      }
      pageProps = Component.getInitialProps
        ? await Component.getInitialProps(ctx, i18n)
        : {};
    } catch (error) {
      console.log(`Error occurred: ${error}`);
      pageProps.statusCode = 500;
      pageProps.seoData = {};
      pageProps.pathname = pathname;
    }

    return {
      pageProps
    };
  }

  componentDidMount() {
    const { apolloClient } = this.props;
    NProgress.configure({ showSpinner: false, trickleSpeed: 50 });
    Router.router.events.on('routeChangeStart', url => {
      if (!url.match(/\/items\/(\d+)/)) {
        NProgress.start();
      }
    });
    onlyProductionContent(() => {
      li();
      tagManager(gtmContainerId);
    });
    Router.router.events.on('routeChangeComplete', () => {
      onlyProductionContent(() => li());
      NProgress.done();
      if (Router.router.state.route === '/') {
        apolloClient.reFetchObservableQueries();
      }
    });
    document.body.classList.remove('preload');
  }

  render() {
    const { Component, pageProps, apolloClient } = this.props;
    const { seoData, statusCode } = pageProps;
    const seoConfig = createSeoConfig(seoData, fbAppId, i18n);
    const getLayout = Component.getLayout || (page => page);

    if (typeof window !== 'undefined') {
      window.adriverExtentionLoad = adriverCallback;
    }

    if (statusCode === 404) {
      return (
        <I18nextProvider i18n={i18n}>
          <ErrorPage statusCode={statusCode} />
        </I18nextProvider>
      );
    }

    return (
      <ThemeProvider theme={getCurrentTheme()}>
        <GlobalStyles />
        <I18nextProvider i18n={i18n}>
          {seoConfig ? (
            <>
              <HeadMeta {...seoData.hostSum} />
              <DefaultSeo {...seoConfig} />
            </>
          ) : null}
          <ApolloProvider client={apolloClient}>
            <BridgeProvider>
              {getLayout(
                <ErrorBoundary
                  statusCode={statusCode}
                  i18n={i18n}
                  router={Router}
                  pageProps={pageProps}
                >
                  <Component {...pageProps} />
                </ErrorBoundary>
              )}
            </BridgeProvider>
          </ApolloProvider>
        </I18nextProvider>
      </ThemeProvider>
    );
  }
}

export default withBridgeState(withApolloClient(reduxWrapper.withRedux(MyApp)));
